import type { FC } from 'react';
import type { TooltipRenderProps } from 'react-joyride';
import { Button } from '@/shared/Button';
import { cn } from '@/shared/react';

export const SpotlightTooltip: FC<TooltipRenderProps> = ({
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  continuous,
  index,
  isLastStep,
  size,
  step,
}) => {
  const showBack = continuous && index > 0;
  const showSkip = !isLastStep && size > 1;

  return (
    <div
      className={cn(
        'react-joyride__tooltip spotlight-pop',
        'flex max-h-full min-h-0 w-full max-w-full flex-col',
        'overflow-visible rounded-3xl border-0 shadow-[0_0_32px_32px_rgba(0,0,0,0.1)]',
        'bg-surface-l0 text-content-primary',
      )}
      style={{ width: 'min(380px, calc(100vw - 32px))' }}
      data-joyride-step={index}
      {...tooltipProps}
    >
      {step.title && (
        <h4
          id="joyride-tooltip-title"
          className="flex-shrink-0 px-4 pt-4 pb-1 text-base font-semibold"
        >
          {step.title}
        </h4>
      )}

      <div
        id="joyride-tooltip-content"
        className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm leading-relaxed text-content-secondary"
      >
        {step.content}
      </div>

      <div className="flex flex-shrink-0 items-center justify-between gap-3 px-4 pt-2 pb-4">
        {size > 1 ? (
          <div className="flex items-center gap-1.5">
            {Array.from({ length: size }).map((_, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: dots have no identity beyond position
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  i === index
                    ? 'w-5 bg-content-brand'
                    : i < index
                      ? 'w-1.5 bg-content-brand/40'
                      : 'w-1.5 bg-border-tertiary',
                )}
              />
            ))}
          </div>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          {showSkip && (
            <Button
              size={8}
              variant="text"
              onClick={skipProps.onClick}
              aria-label={skipProps['aria-label']}
            >
              {skipProps.title}
            </Button>
          )}
          {showBack && (
            <Button
              size={8}
              variant="ghost"
              onClick={backProps.onClick}
              aria-label={backProps['aria-label']}
            >
              {backProps.title}
            </Button>
          )}
          <Button
            size={8}
            variant="brand"
            onClick={primaryProps.onClick}
            aria-label={primaryProps['aria-label']}
          >
            {primaryProps.title}
          </Button>
        </div>
      </div>
    </div>
  );
};
